import { useEffect, useState } from 'react'
import type { router as AppRouter } from '../router'
import { Cookie } from 'lucide-react'
import type { Language } from '../publicI18n'
import { initializeGoogleAnalytics, setGoogleConsent, trackPublicPageView } from '../utils/googleAnalytics'
import {
  isPublicSitePath,
  readTrackingConsent,
  TRACKING_CONSENT_EVENT,
  type TrackingConsent,
  writeTrackingConsent,
} from '../utils/trackingConsent'

const measurementId = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim() ?? ''

const COPY: Record<Language, {
  title: string
  body: string
  accept: string
  reject: string
  manage: string
  save: string
  analytics: string
  analyticsBody: string
  advertising: string
  advertisingBody: string
  required: string
  requiredBody: string
}> = {
  tr: {
    title: 'Gizlilik tercihleri',
    body: 'Site kullanımını ölçmek ve blog reklamlarını göstermek için isteğe bağlı teknolojiler kullanıyoruz. Seçiminizi dilediğiniz zaman değiştirebilirsiniz.',
    accept: 'Tümünü kabul et', reject: 'Tümünü reddet', manage: 'Tercihleri yönet', save: 'Tercihleri kaydet',
    analytics: 'Analitik', analyticsBody: 'Landing, doküman ve blog sayfalarındaki anonim kullanım eğilimlerini ölçer.',
    advertising: 'Reklam', advertisingBody: 'Blog yazılarının kenarlarındaki reklam alanlarını etkinleştirir.',
    required: 'Zorunlu', requiredBody: 'Dil, tema ve güvenlik tercihleri için gereklidir; kapatılamaz.',
  },
  en: {
    title: 'Privacy preferences',
    body: 'We use optional technologies to measure site usage and show blog ads. You can change your choice at any time.',
    accept: 'Accept all', reject: 'Reject all', manage: 'Manage preferences', save: 'Save preferences',
    analytics: 'Analytics', analyticsBody: 'Measures anonymous usage trends across the landing, docs and blog pages.',
    advertising: 'Advertising', advertisingBody: 'Enables the ad placements beside blog posts.',
    required: 'Required', requiredBody: 'Needed for language, theme and security preferences; it cannot be disabled.',
  },
  de: {
    title: 'Datenschutzeinstellungen',
    body: 'Wir verwenden optionale Technologien, um die Websitenutzung zu messen und Blog-Werbung anzuzeigen. Sie können Ihre Auswahl jederzeit ändern.',
    accept: 'Alle akzeptieren', reject: 'Alle ablehnen', manage: 'Einstellungen verwalten', save: 'Auswahl speichern',
    analytics: 'Analyse', analyticsBody: 'Misst anonyme Nutzungstrends auf Landingpage, Dokumentation und Blog.',
    advertising: 'Werbung', advertisingBody: 'Aktiviert die Werbeflächen neben Blogbeiträgen.',
    required: 'Erforderlich', requiredBody: 'Für Sprache, Darstellung und Sicherheit erforderlich; kann nicht deaktiviert werden.',
  },
  fr: {
    title: 'Préférences de confidentialité',
    body: 'Nous utilisons des technologies facultatives pour mesurer l’utilisation du site et afficher des publicités sur le blog. Vous pouvez modifier votre choix à tout moment.',
    accept: 'Tout accepter', reject: 'Tout refuser', manage: 'Gérer les préférences', save: 'Enregistrer',
    analytics: 'Analyse', analyticsBody: 'Mesure les tendances d’utilisation anonymes des pages d’accueil, de documentation et du blog.',
    advertising: 'Publicité', advertisingBody: 'Active les emplacements publicitaires à côté des articles du blog.',
    required: 'Nécessaire', requiredBody: 'Requis pour la langue, le thème et la sécurité; ne peut pas être désactivé.',
  },
}

export function PublicSiteTelemetry({ router, language }: { router?: typeof AppRouter; language: Language }) {
  const copy = COPY[language]
  const [pathname, setPathname] = useState(router?.state.location.pathname ?? window.location.pathname)
  const [consent, setConsent] = useState<TrackingConsent | null>(() => readTrackingConsent())
  const [preferencesOpen, setPreferencesOpen] = useState(false)
  const [draft, setDraft] = useState<TrackingConsent>(consent ?? { analytics: false, advertising: false })

  useEffect(() => router?.subscribe(state => setPathname(state.location.pathname)), [router])

  useEffect(() => {
    const onConsent = (event: Event) => setConsent((event as CustomEvent<TrackingConsent>).detail)
    window.addEventListener(TRACKING_CONSENT_EVENT, onConsent)
    return () => window.removeEventListener(TRACKING_CONSENT_EVENT, onConsent)
  }, [])

  useEffect(() => {
    if (!measurementId) return
    if (!consent?.analytics) {
      if (window.gtag && consent) setGoogleConsent(consent)
      return
    }

    setGoogleConsent(consent)
    initializeGoogleAnalytics(measurementId)
    const sendPageView = () => trackPublicPageView(
      measurementId,
      router?.state.location.pathname ?? window.location.pathname,
      router?.state.location.search ?? window.location.search,
    )
    sendPageView()
    return router?.subscribe(state => {
      if (state.navigation.state === 'idle') window.setTimeout(sendPageView, 0)
    })
  }, [consent, router])

  if (!isPublicSitePath(pathname)) return null

  const choose = (next: TrackingConsent) => {
    writeTrackingConsent(next)
    setConsent(next)
    setPreferencesOpen(false)
  }

  if (consent && !preferencesOpen) {
    return (
      <button
        type="button"
        aria-label={copy.title}
        title={copy.title}
        onClick={() => {
          setDraft(consent)
          setPreferencesOpen(true)
        }}
        className="fixed bottom-3 left-3 z-[100] inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-lg transition hover:border-gray-300 hover:text-gray-900 sm:bottom-5 sm:left-5"
      >
        <Cookie size={17} aria-hidden="true" />
      </button>
    )
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-5"
      role={preferencesOpen ? 'dialog' : 'region'}
      aria-modal={preferencesOpen || undefined}
      aria-labelledby="tracking-consent-title"
    >
      <div className="mx-auto max-w-4xl rounded-3xl border border-gray-200 bg-white p-5 text-gray-900 shadow-[0_24px_80px_rgba(15,23,42,0.22)] sm:p-6">
        <h2 id="tracking-consent-title" className="text-[16px] font-semibold">{copy.title}</h2>
        <p className="mt-2 max-w-3xl text-[13px] leading-relaxed text-gray-600">{copy.body}</p>

        {preferencesOpen && (
          <div className="mt-5 grid gap-3 border-t border-gray-100 pt-5 sm:grid-cols-3">
            <ConsentChoice title={copy.required} body={copy.requiredBody} checked disabled onChange={() => {}} />
            <ConsentChoice title={copy.analytics} body={copy.analyticsBody} checked={draft.analytics} onChange={checked => setDraft(value => ({ ...value, analytics: checked }))} />
            <ConsentChoice title={copy.advertising} body={copy.advertisingBody} checked={draft.advertising} onChange={checked => setDraft(value => ({ ...value, advertising: checked }))} />
          </div>
        )}

        <div className="mt-5 grid gap-2 sm:grid-cols-3">
          <button type="button" onClick={() => choose({ analytics: true, advertising: true })} className="rounded-full bg-[#15803D] px-5 py-2.5 text-[13px] font-semibold text-white transition hover:bg-[#16A34A]">{copy.accept}</button>
          <button type="button" onClick={() => choose({ analytics: false, advertising: false })} className="rounded-full border border-gray-300 px-5 py-2.5 text-[13px] font-semibold text-gray-800 transition hover:bg-gray-50">{copy.reject}</button>
          <button type="button" onClick={() => preferencesOpen ? choose(draft) : setPreferencesOpen(true)} className="rounded-full border border-gray-300 px-5 py-2.5 text-[13px] font-semibold text-gray-800 transition hover:bg-gray-50">{preferencesOpen ? copy.save : copy.manage}</button>
        </div>
      </div>
    </div>
  )
}

function ConsentChoice({ title, body, checked, disabled = false, onChange }: {
  title: string
  body: string
  checked: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={event => onChange(event.target.checked)} className="mt-0.5 h-4 w-4 accent-[#16A34A]" />
      <span>
        <span className="block text-[13px] font-semibold">{title}</span>
        <span className="mt-1 block text-[11px] leading-relaxed text-gray-500">{body}</span>
      </span>
    </label>
  )
}
